<template>
  <div id="share-lti-panel" class="panel-container is-animated slide-from-right">
    <header class="panel-header">
      <div class="panel-left-item">
        <base-icon class="icon-button" name="arrow_back" @click="backToMain" />
      </div>
      <h3>{{ __('Add to LMS') }}</h3>
      <div class="panel-right-item" />
    </header>
    <div class="panel-body">
      <div v-if="isPasswordProtected && !willRemakeForLtiUse" class="tv-cell tv-cell-subtitle warning-scheme">
        <base-icon class="caption-type" name="warning" />
        <div>
          <strong>{{ __('This padlet is password protected.') }}</strong>
          <span>{{
            __('People without access privileges will be asked for a password before they can post on it.')
          }}</span>
        </div>
      </div>
      <div v-if="isPrivate && !willRemakeForLtiUse" class="tv-cell tv-cell-subtitle warning-scheme">
        <base-icon class="caption-type" name="warning" />
        <div>
          <strong>{{ __('This padlet is private.') }}</strong>
          <span> {{ __('Only people with access privileges will be able to view and/or post on it.') }} </span>
        </div>
      </div>
      <section class="custom-params-section">
        <header class="section-header">
          <div>{{ __('Behavior') }}</div>
        </header>
        <div class="section-body">
          <div class="section-list list">
            <div class="list-item" @click="changeLtiWillRemake('no')">
              <div class="list-item-primary">
                <base-icon v-if="willRemakeForLtiUse" name="radio_button_unchecked" />
                <base-icon v-else class="is-highlighted" name="radio_button_checked" />
                <div class="list-item-text">
                  <div>{{ __('Everyone works on this padlet.') }}</div>
                </div>
              </div>
            </div>
            <div class="list-item" @click="changeLtiWillRemake('yes')">
              <div class="list-item-primary">
                <base-icon v-if="!willRemakeForLtiUse" name="radio_button_unchecked" />
                <base-icon v-else class="is-highlighted" name="radio_button_checked" />
                <div class="list-item-text">
                  <div>{{ __('Everyone works on their own copy of this padlet.') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="manual-config-section">
        <header class="section-header">
          <div>{{ __('Enter the following information in your LMS') }}</div>
        </header>
        <div class="section-body">
          <div class="section-list list">
            <div class="list-item">
              <div class="label-textfield-group">
                <label
                  ><span> {{ __('(Secure) Launch/Tool URL') }} </span>&nbsp;&nbsp;<a
                    :data-label="__('(Secure) Launch/Tool URL')"
                    @click="copyLtiOptionValue(ltiCallbackUrl)"
                    ><span>{{ __('Copy') }}</span></a
                  ></label
                >
                <input dir="auto" disabled="disabled" type="text" :value="ltiCallbackUrl" />
              </div>
            </div>
            <div class="list-item">
              <div class="label-textfield-group">
                <label
                  ><span>{{ __('Consumer key') }}</span
                  >&nbsp;&nbsp;<a :data-label="__('Consumer key')" @click="copyLtiOptionValue(ltiConsumerKey)"
                    ><span>{{ __('Copy') }}</span></a
                  ></label
                >
                <input dir="auto" disabled="disabled" type="text" :value="ltiConsumerKey" />
              </div>
            </div>
            <div class="list-item">
              <div class="label-textfield-group">
                <label
                  ><span>{{ __('Shared secret') }}</span
                  >&nbsp;&nbsp;<a :data-label="__('Shared secret')" @click="copyLtiOptionValue(ltiSharedSecret)"
                    ><span>{{ __('Copy') }}</span></a
                  ></label
                >
                <input dir="auto" disabled="disabled" type="text" :value="ltiSharedSecret" />
              </div>
            </div>
            <div class="list-item">
              <div class="label-textfield-group">
                <label
                  ><span>{{ __('Icon URL') }}</span
                  >&nbsp;&nbsp;<a
                    :data-label="__('Icon URL')"
                    @click="copyLtiOptionValue('https://padlet.com/logo.png')"
                    ><span>{{ __('Copy') }}</span></a
                  ></label
                >
                <input dir="auto" disabled="disabled" type="text" :value="'https://padlet.com/logo.png'" />
              </div>
            </div>
            <div class="list-item">
              <div class="label-textfield-group">
                <label
                  ><span>{{ __('XML Config URL') }}</span
                  >&nbsp;&nbsp;<a :data-label="__('XML Config URL')" @click="copyLtiOptionValue(ltiConfigUrl)"
                    ><span>{{ __('Copy') }}</span></a
                  ></label
                >
                <input dir="auto" disabled="disabled" type="text" :value="ltiConfigUrl" />
              </div>
            </div>
            <div class="list-item">
              <div class="label-textfield-group">
                <label
                  ><span>{{ __('Custom parameters') }}</span
                  >&nbsp;&nbsp;<a
                    :data-label="__('Custom parameters')"
                    @click="copyLtiOptionValue(ltiCustomParamsAsText)"
                    ><span>{{ __('Copy') }}</span></a
                  ></label
                >
                <textarea id="lti-custom-params-textarea" disabled="disabled" v-text="ltiCustomParamsAsText"></textarea>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapGetters, mapActions } from 'vuex'
import BaseIcon from 'vuecomponents/base_icon'
import { $ } from 'bits/jquery'
import { copyToClipboard } from 'bits/clipboard'
import { ShareSubpanel } from 'vuexstore/modules/share'

export default {
  components: { BaseIcon },
  data() {
    return {
      willRemakeForLtiUse: false,
      ltiCustomParams: {},
      ltiCustomParamsAsText: '',
      ltiConfigUrl: '',
    }
  },
  computed: {
    ...mapState({
      ltiCallbackUrl: (state) => state.constants.ltiCallbackUrl,
      baseLtiConfigUrl: (state) => state.constants.baseLtiConfigUrl,
      ltiConsumerKey: (state) => state.constants.ltiConsumer && state.constants.ltiConsumer.key,
      ltiSharedSecret: (state) => state.constants.ltiConsumer && state.constants.ltiConsumer.sharedSecret,
    }),
    ...mapGetters(['domainName', 'publicKey', 'isPrivate', 'isPasswordProtected']),
  },
  mounted() {
    this.ltiCustomParams = { public_key: this.publicKey }
    this.ltiConfigUrl = this.baseLtiConfigUrl
      .replace('_tenantDomainName_', this.domainName)
      .replace('_wallPublicKey_', this.publicKey)
    this.modifyLtiCustomParams()
  },
  methods: {
    ...mapActions('share', ['changeActivePanel']),
    ...mapActions('snackbars', ['setSnackbar']),
    backToMain() {
      this.changeActivePanel(ShareSubpanel.Main)
    },
    async copyLtiOptionValue(stringToCopy) {
      const result = await copyToClipboard(stringToCopy || '')
      if (result) {
        this.setSnackbar({ message: this.__('Copied to your clipboard.') })
      } else {
        this.setSnackbar({ message: this.__('Could not copy to your clipboard.') })
      }
    },
    modifyLtiCustomParams(options) {
      this.ltiCustomParams = { ...this.ltiCustomParams, ...options }
      this.ltiConfigUrl = this.baseLtiConfigUrl
        .replace('_tenantDomainName_', this.domainName)
        .replace('_wallPublicKey_', this.publicKey)
      let newParamsAsText = ''
      Object.keys(this.ltiCustomParams).forEach((name) => {
        const value = this.ltiCustomParams[name]
        newParamsAsText += name + '=' + value + '\n'
        if (name !== 'public_key') {
          this.ltiConfigUrl += '&' + name + '=' + value
        }
      })
      this.ltiCustomParamsAsText = newParamsAsText
      this.willRemakeForLtiUse = this.ltiCustomParams.remake === 'yes'
    },
    changeLtiWillRemake(newValue) {
      this.modifyLtiCustomParams({ remake: newValue })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.list > .list-item > .list-item-primary {
  align-items: center;
}
.custom-params-section {
  .list-item:hover {
    cursor: pointer;
  }
  .list-item.is-disabled {
    color: $muted-text-color;

    .list-item-text {
      color: $muted-text-color;
      * {
        display: inline;
      }
    }

    i.material-icons {
      color: $muted-text-color;
    }

    .list-item-text:after {
      content: '*';
      margin-left: 0.25em;
    }

    &:hover {
      cursor: default;
      background-color: inherit;
    }
  }
  .message {
    @include small-type;
  }

  .message:before {
    content: '*';
    padding-right: 0.25em;
  }
}
.manual-config-section {
  a {
    cursor: pointer;
  }
}
</style>
